import React, { useState } from 'react';
import SparkleIcon from './icons/SparkleIcon';

export interface WorkflowSuggestion {
  workflowType: string;
  displayName: string;
  reason: string;
  documentIds: string[];
  prefillContext: string;
  score: number;
  suggestionKey: string;
}

interface WorkflowSuggestionCardsProps {
  suggestions: WorkflowSuggestion[];
  /** documentId -> display name, for the doc chips under each card. Missing ids are silently skipped. */
  documentNames: Map<string, string>;
  isList?: boolean;
  highlightedSuggestionKeys?: Set<string>;
  onRun: (s: WorkflowSuggestion) => void;
  /** Omit to hide the Dismiss affordance entirely (e.g. a one-time post-intake recommendation has nothing standing to dismiss). */
  onDismiss?: (s: WorkflowSuggestion, reason?: 'wrong_documents' | 'already_done' | 'not_relevant') => void;
  title?: string;
  className?: string;
}

// Extracted from ProjectDetailView's "Recommended for this project" band
// (SMART_INTAKE_PLAN.md Phase 2) so intake's post-commit recommendations and
// a project's own recommendation band render identically instead of
// duplicating this markup.
const WorkflowSuggestionCards: React.FC<WorkflowSuggestionCardsProps> = ({
  suggestions,
  documentNames,
  isList = false,
  highlightedSuggestionKeys,
  onRun,
  onDismiss,
  title = 'Recommended for this project',
  className = '',
}) => {
  const [pickingDismissReasonFor, setPickingDismissReasonFor] = useState<string | null>(null);

  if (suggestions.length === 0) return null;

  return (
    <div className={className}>
      {title && (
        <h3 className="text-lg font-semibold text-secondary dark:text-dark-text mb-3 flex items-center gap-1.5">
          <SparkleIcon className="w-4 h-4 text-primary" />
          {title}
        </h3>
      )}
      <div className={isList ? 'flex flex-col gap-2' : 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4'}>
        {suggestions.map((s) => {
          const matchedNames = s.documentIds.map((id) => documentNames.get(id)).filter((n): n is string => !!n);
          const visibleNames = matchedNames.slice(0, 4);
          const extraDocCount = matchedNames.length - visibleNames.length;
          const isHighlighted = highlightedSuggestionKeys?.has(s.suggestionKey) ?? false;

          return (
            <div key={s.suggestionKey} className={`bg-gradient-to-br from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20 border border-primary/30 dark:border-primary/40 rounded-lg p-4 ${isHighlighted ? 'ring-2 ring-primary' : ''}`}>
              <div className="flex items-start justify-between gap-2">
                <h4 className="font-semibold text-secondary dark:text-dark-text">{s.displayName}</h4>
                {s.score >= 90 && (
                  <span className="flex-shrink-0 text-[10px] font-medium uppercase tracking-wide text-primary bg-primary/10 rounded-full px-2 py-0.5">
                    Strong match
                  </span>
                )}
              </div>
              <p className="text-sm text-accent dark:text-dark-muted mt-1">{s.reason}</p>
              {matchedNames.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {visibleNames.map((name) => (
                    <span
                      key={name}
                      title={name}
                      className="text-xs text-secondary dark:text-dark-text bg-white/70 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 rounded px-2 py-0.5 truncate max-w-[10rem]"
                    >
                      {name}
                    </span>
                  ))}
                  {extraDocCount > 0 && (
                    <span className="text-xs text-accent dark:text-dark-muted px-2 py-0.5">+{extraDocCount} more</span>
                  )}
                </div>
              )}
              {onDismiss && pickingDismissReasonFor === s.suggestionKey ? (
                <div className="flex items-center flex-wrap gap-2 mt-3">
                  <button
                    onClick={() => onDismiss(s, 'wrong_documents')}
                    className="px-2 py-1 text-xs bg-white/70 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 rounded-full text-secondary dark:text-dark-text hover:bg-white dark:hover:bg-gray-800 transition-colors"
                  >
                    Wrong documents
                  </button>
                  <button
                    onClick={() => onDismiss(s, 'already_done')}
                    className="px-2 py-1 text-xs bg-white/70 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 rounded-full text-secondary dark:text-dark-text hover:bg-white dark:hover:bg-gray-800 transition-colors"
                  >
                    Already done
                  </button>
                  <button
                    onClick={() => onDismiss(s, 'not_relevant')}
                    className="px-2 py-1 text-xs bg-white/70 dark:bg-gray-800/70 border border-gray-200 dark:border-gray-700 rounded-full text-secondary dark:text-dark-text hover:bg-white dark:hover:bg-gray-800 transition-colors"
                  >
                    Not relevant
                  </button>
                  <button
                    onClick={() => onDismiss(s)}
                    title="Dismiss without a reason"
                    className="px-2 py-1 text-xs text-accent dark:text-dark-muted hover:text-secondary dark:hover:text-dark-text transition-colors"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <div className="flex items-center gap-3 mt-3">
                  <button
                    onClick={() => onRun(s)}
                    className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Run with these documents
                  </button>
                  {onDismiss && (
                    <button
                      onClick={() => setPickingDismissReasonFor(s.suggestionKey)}
                      className="px-2 py-1.5 text-sm text-accent dark:text-dark-muted hover:text-secondary dark:hover:text-dark-text transition-colors"
                    >
                      Dismiss
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default WorkflowSuggestionCards;
